import http, { isErrorToastedByHttpClient } from '@/lib/http'
import { parseQuestionnaire } from '@/lib/parseQuestionnaire'
import { type Flag, useGetFlags } from '@/services/flags'
import { messagingHttp } from '@/services/messaging'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from 'notistack'
import { useMemo } from 'react'

// ── Transformed (frontend) types ──────────────────────────────────────

export interface ParsedQuestionnaire {
  answer: string | string[]
  description: string | null
  flags: string[]
  product: string
  link?: string
  links?: string[]
  hasFlags?: boolean
  questionnaire: string
  timestamp: number
  title: string
  type: string
  hash: string
  batch?: boolean
  imageUploaded?: boolean
}

export interface SafeguardingQuestion {
  question: string
  answer: string
}

export interface AdditionalNotes {
  prescriber?: string
  pharmacist?: string
  customerService?: string
}

export interface OnHoldEntry {
  reason: string
  patientResponse?: string
  date: string
}

export interface RefundEntry {
  reason: string
  date: string
}

export interface MedicationRequestPatient {
  id: string
  linkedPatientHash: string | null
  firstName: string
  lastName: string
  userHash?: string
  dob: string
  gender: string
  scrData: string | null
  familyHistory: string | null
  allergies: string | null
  conditions: string | null
  medications: string | null
  socialHistory: string | null
  otherObservations: string | null
  mobile: string | null
  email: string
  summaryCareRecordSyncedAt: string | null
  redFlagReason: string | null
  safeguardingQuestions: Record<string, string> | null
}

export interface MedicationRequest {
  id: string
  orderHash: string
  evaroType: string | null
  orderNumber: string
  orderPhone: string
  orderCountry?: string
  stage: string
  vendor_hash: string
  customerEmail: string
  patient: MedicationRequestPatient
  orderPostCode: string
  orderedItem: {
    id: string
    category: string
    originalProductVariationHash: string
    originalProductHash: string
    hasBeenChanged?: boolean
    name: string
    dose: string
    directions: string
    quantity: string
    package?: string
    productVariationSku?: string
    productVariationQuantity?: string
  }
  prescriber: {
    id: string
    firstName: string
    lastName: string
    fullName: string
  }
  prescriptionIssueDate: string
  prescriptionUrl: string | null
  reasonForRejection?: string | null
  reasonForApproval?: string | null
  reasonForHold?: string | null
  consultationData: ParsedQuestionnaire[] // Derived from the `questionaire` field.
  safeguardingQuestions?: SafeguardingQuestion[]
  additionalNotes: AdditionalNotes
  epharmacyVendor: string
  closingDate: string

  lastUpdateOn: string | null
  orderProcessType?: string
  productGroup?: string | null

  // New fields
  onHoldHistory?: OnHoldEntry[] // TODO: integrate when they are implemented in the PHP API
  refundHistory?: RefundEntry[] // TODO: integrate when they are implemented in the PHP API
  creationDate: string
  approvedBy?: string | null
  gp?: {
    hash: string
    name: string
    email: string
    address_line_1: string
    address_line_2: string
    address_line_3: string
    address_line_4: string
    postcode: string
    county?: string
  } | null
  prescribedItemName?: string | null
}

const DEFAULT_PATCHS_VENDOR_HASHES = ['7a99527ae705fe76a25f1d70cf802b']

// ── Raw API types ─────────────────────────────────────────────────────

export type MedRequestPatientType = {
  id: string
  linkedPatientHash: string | null
  firstName: string
  lastName: string
  userHash?: string
  dob: string
  gender: string
  scrData: string | null
  familyHistory: string | null
  allergies: string | null
  conditions: string | null
  medications: string | null
  socialHistory: string | null
  otherObservations: string | null
  mobile: string | null
  email: string
  summaryCareRecordSyncedAt: string | null
  redFlagReason: string | null
  safeguardingQuestions: Record<string, string> | null
}

export interface MedRequestOrderedItem {
  id: string
  category: string
  originalProductVariationHash: string
  originalProductHash: string
  hasBeenChanged?: boolean
  name: string
  dose: string
  directions: string
  quantity: string
  package?: string
  productVariationSku?: string
  productVariationQuantity?: string
}

export type AdditionalNotesType = {
  pharmacist?: string | null
  customerService?: string | null
}

export interface MedRequest {
  prescriptionUrl: string | null
  patient: MedRequestPatientType

  safeguardingQuestions?: Record<string, string> | null
  latestFanConversationId: string | null
  rejectionConversationId: string | null

  vendor_hash: string
  customerHash: string
  customerEmail: string
  orderAddress: string
  orderPhone: string
  orderPostCode: string
  orderCountry?: string | null
  orderNumber: string
  shippingMethod: string
  prescriptionIssueDate: string
  rejectedAt: string | null
  orderHash: string
  evaroType?: string | null
  juniorPrescriberRejected: boolean

  prescriber: {
    id: string | null
    firstName: string | null
    lastName: string | null
    fullName: string | null
  } // This is not implemented yet.

  tags: string[] // This is not implemented yet.

  orderMedNameString: string
  prescribedItemName?: string | null
  wooSKU: string
  OTC: boolean

  orderedItem: MedRequestOrderedItem

  stage: string
  questionaire: string

  trainingMode: boolean
  epharmacyVendor: string

  creationDate: string
  closingDate: string
  ewfSentDate: string
  prescriptionPrintedAt: string

  idVerifiedTime: string

  additionalNotes: AdditionalNotesType
  reasonForRejection?: string | null
  reasonForApproval?: string | null // This is missing
  reasonForHold: string | null
  patientsResponseForHold: string | null

  isReorder: boolean
  isPriority: boolean
  orderProcessType: string | null
  productGroup: string | null

  subscriptionFrequency: string | null
  consultationAISummary: string | null
  lastUpdateOn: string | null

  wooOrderId?: string | null // This is missing.

  onHoldHistory?: Array<{ reason: string; patientResponse?: string | null; date: string }> | null
  approvedBy?: string | null

  gp?: {
    hash: string
    name: string
    email: string
    address_line_1?: string
    address_line_2?: string
    address_line_3?: string
    address_line_4?: string
    postcode?: string
    county?: string
  } | null

  id: string
}

// ── Update details payload ───────────────────────────────────────────

export interface UpdateMedReqDetailsPayload {
  notes?: string
  cs_notes?: string
  ai_output_n8n?: string
  approved_at?: string
  approved_by?: string
  cedarx_product_id?: string
  closing_date?: string
  directions?: string
  junior_prescriber_rejected?: boolean
  justification?: string
  last_action_taken_by?: string
  latest_fan_conversation_id?: string
  on_hold_history?: string
  original_ordered_item_product_hash?: string
  original_ordered_item_variation_hash?: string
  prescriber?: string
  reason_for_rejection?: string
  rejected_at?: string
  rejection_conversation_id?: string
  reason_for_hold?: string
  scr_summary_prompt?: string
  stage?: string
}

export interface SendVideoConsultationSmsResponse {
  data: {
    success: boolean
  }
}

export type SendVideoConsultationSmsPayload = {
  to: string
  vendorHash: string
  patientName: string
}

// ── Service functions & hooks ─────────────────────────────────────────

export const fetchMedReqByOrderItemHash = async (hash: string): Promise<MedRequest> => {
  return (await http.get(`/med-reqs/cs-view/${hash}`)) as MedRequest
}

export function toMedicationRequest(raw: MedRequest, flagsMap: Record<string, Flag>): MedicationRequest {
  const consultationData = raw.questionaire ? parseQuestionnaire(raw.questionaire, flagsMap) : []

  let onHoldHistory: OnHoldEntry[] = []
  if (Array.isArray(raw.onHoldHistory)) {
    onHoldHistory = raw.onHoldHistory.map(entry => ({
      reason: entry.reason ?? '',
      patientResponse: entry.patientResponse ?? undefined,
      date: entry.date ?? ''
    }))
  }
  // Fallback to legacy reasonForHold/patientsResponseForHold when onHoldHistory is not available
  if (!onHoldHistory.length && raw.reasonForHold) {
    onHoldHistory = [{ reason: raw.reasonForHold, patientResponse: raw.patientsResponseForHold ?? undefined, date: '' }]
  }

  const safeguardingQuestions: SafeguardingQuestion[] | undefined = raw.safeguardingQuestions
    ? Object.entries(raw.safeguardingQuestions).map(([question, answer]) => ({ question, answer }))
    : undefined

  return {
    id: raw.id,
    orderHash: raw.orderHash,
    evaroType: raw.evaroType ?? null,
    orderNumber: raw.orderNumber,
    orderPhone: raw.orderPhone,
    orderCountry: raw.orderCountry ?? undefined,
    stage: raw.stage,
    vendor_hash: raw.vendor_hash,
    customerEmail: raw.customerEmail ?? '',
    orderPostCode: raw.orderPostCode,

    patient: {
      ...raw.patient,
      firstName: raw.patient.firstName ?? '',
      lastName: raw.patient.lastName ?? '',
      dob: raw.patient.dob ?? '',
      gender: raw.patient.gender ?? '',
      email: raw.patient.email ?? raw.customerEmail ?? ''
    },

    orderedItem: {
      ...raw.orderedItem,
      originalProductVariationHash: raw.orderedItem.originalProductVariationHash ?? '',
      originalProductHash: raw.orderedItem.originalProductHash ?? ''
    },

    prescriber: {
      id: raw.prescriber.id ?? '',
      firstName: raw.prescriber.firstName ?? '',
      lastName: raw.prescriber.lastName ?? '',
      fullName: raw.prescriber.fullName ?? ''
    },

    prescriptionIssueDate: raw.prescriptionIssueDate,
    prescriptionUrl: raw.prescriptionUrl,
    reasonForRejection: raw.reasonForRejection,
    reasonForApproval: raw.reasonForApproval,
    reasonForHold: raw.reasonForHold,
    consultationData,
    safeguardingQuestions,
    additionalNotes: {
      pharmacist: raw.additionalNotes?.pharmacist ?? undefined,
      customerService: raw.additionalNotes?.customerService ?? undefined
    },
    epharmacyVendor: raw.epharmacyVendor,
    closingDate: raw.closingDate,
    creationDate: raw.creationDate,
    orderProcessType: raw.orderProcessType ?? undefined,
    productGroup: raw.productGroup,

    onHoldHistory,
    lastUpdateOn: raw.lastUpdateOn,
    refundHistory: [], // TODO: Implement this when the API is implemented.
    approvedBy: raw.approvedBy ?? undefined,
    gp: raw.gp
      ? {
          hash: raw.gp.hash,
          name: raw.gp.name,
          email: raw.gp.email,
          address_line_1: raw.gp.address_line_1 ?? '',
          address_line_2: raw.gp.address_line_2 ?? '',
          address_line_3: raw.gp.address_line_3 ?? '',
          address_line_4: raw.gp.address_line_4 ?? '',
          postcode: raw.gp.postcode ?? ''
        }
      : null,
    prescribedItemName: raw.prescribedItemName ?? null
  }
}

export const useMedReqDetailQuery = (orderItemHash: string | undefined) => {
  const { data: flagsMap } = useGetFlags()

  const {
    data: rawData,
    isLoading: isRawLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['med-request-detail', orderItemHash],
    queryFn: () => fetchMedReqByOrderItemHash(orderItemHash!),
    enabled: !!orderItemHash
  })

  const data = useMemo(() => {
    if (!rawData || !flagsMap) return undefined
    return toMedicationRequest(rawData, flagsMap)
  }, [rawData, flagsMap])

  const isLoading = isRawLoading || (!!rawData && !flagsMap)

  return { data, isLoading, isError, error }
}

/**
 * Update medication request details via the generic update endpoint.
 * @param orderItemHash - Order item hash (used as the med-request identifier)
 * @param data - Partial payload of fields to update
 */
export const updateMedReqDetails = async (
  orderItemHash: string,
  data: UpdateMedReqDetailsPayload
): Promise<{ code: string; message: string }> => {
  return (await http.post(`/med-reqs/${orderItemHash}/update-details`, data)) as {
    code: string
    message: string
  }
}

// Not idempotent and http.tsx retries POSTs: a retried timeout would restart the workflow twice.
export const resetIdVerification = async (orderItemHash: string, reason?: string): Promise<void> => {
  await http.post(
    `/med-reqs/${orderItemHash}/reset-id-verification`,
    { ...(reason?.trim() ? { reason: reason.trim() } : {}) },
    { 'axios-retry': { retries: 0 } }
  )
}

export const sendVideoConsultationSms = async ({
  to,
  vendorHash,
  patientName
}: SendVideoConsultationSmsPayload): Promise<SendVideoConsultationSmsResponse> => {
  return (await messagingHttp.post('/api/sms/patchs-video-call', {
    to,
    vendorHash,
    data: { patientName }
  })) as SendVideoConsultationSmsResponse
}

export const isPatchsPharmacyFirstMedReq = (
  medReq: Pick<MedicationRequest, 'vendor_hash' | 'orderProcessType'>
): boolean => {
  const vendorHash = medReq.vendor_hash?.toLowerCase().trim() ?? ''

  return DEFAULT_PATCHS_VENDOR_HASHES.includes(vendorHash)
}

/**
 * Mutation hook for updating medication request details.
 */
export const useUpdateMedReqDetailsMutation = () => {
  return useMutation({
    mutationFn: ({ orderItemHash, data }: { orderItemHash: string; data: UpdateMedReqDetailsPayload }) =>
      updateMedReqDetails(orderItemHash, data)
  })
}

const invalidateAfterIdVerificationReset = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['med-request-detail'] })
  queryClient.invalidateQueries({ queryKey: ['med-requests'] })
  queryClient.invalidateQueries({ queryKey: ['patient'] })
  queryClient.invalidateQueries({ queryKey: ['patients'] })
  queryClient.invalidateQueries({ queryKey: ['timeline'] })
}

export const useResetIdVerificationMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ orderItemHash, reason }: { orderItemHash: string; reason?: string }) =>
      resetIdVerification(orderItemHash, reason),
    onSuccess: () => {
      invalidateAfterIdVerificationReset(queryClient)
      enqueueSnackbar('Order sent back to System ID Check and ID verified cleared', { variant: 'success' })
    },
    onError: error => {
      if (!isErrorToastedByHttpClient(error)) {
        enqueueSnackbar('Could not confirm the reset — check the timeline before trying again', { variant: 'error' })
      }
      invalidateAfterIdVerificationReset(queryClient)
    }
  })
}

export const useSendVideoConsultationSmsMutation = () => {
  return useMutation({
    mutationFn: (payload: SendVideoConsultationSmsPayload) => sendVideoConsultationSms(payload)
  })
}
